import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import SpeedIcon from "@mui/icons-material/Speed";
import PersonIcon from "@mui/icons-material/Person";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";

import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import CentroEquipoDetalleDialog from "../../requerimiento/components/CentroEquipoDetalleDialog";
import ConfirmDeleteDialog from "../../leasecontrol/components/ConfirmDeleteDialog";
import { listarVehiculos, listarVehiculosSinUsuarios, crearVehiculo, editarVehiculo, eliminarVehiculo, listarIconos } from "../services/trsRastreoService";

const emptyCreateForm = { veh_alias: "", veh_placa: "", veh_modid: "", veh_observaciones: "", veh_icono: "", veh_activo: true };

const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "0.78rem",
  padding: "6px 8px",
  backgroundColor: theme.palette.mode === "dark"
    ? theme.palette.grey[800] : theme.palette.grey[100],
}));

const cellSx = { py: 0.6, px: 1, fontSize: "0.8rem" };

const compactFieldSx = {
  "& .MuiInputBase-root": { minHeight: 32, fontSize: "0.8rem" },
  "& .MuiInputBase-input": { py: 0.65 },
  "& .MuiInputLabel-root": { fontSize: "0.78rem" },
};

const getErrorMessage = (error) =>
  error?.response?.data?.error || error?.response?.data?.message || error?.message || "No se pudo cargar la informacion";

const formatFecha = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("es-BO", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function TrsRastreoVehiculosPage() {
  const [vehiculos, setVehiculos] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [buscar, setBuscar] = useState("");
  const [buscarActivo, setBuscarActivo] = useState("");
  const [detalle, setDetalle] = useState(null);
  const [mapaVehiculo, setMapaVehiculo] = useState(null);
  const [equipoDialog, setEquipoDialog] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [creando, setCreando] = useState(false);
  const [errorCrear, setErrorCrear] = useState(null);
  const [editId, setEditId] = useState(null);
  const [eliminar, setEliminar] = useState(null); // vehiculo a eliminar | null
  const [eliminando, setEliminando] = useState(false);
  const [errorEliminar, setErrorEliminar] = useState("");
  const [iconos, setIconos] = useState([]);
  const [iconosCargados, setIconosCargados] = useState(false);
  const [iconosLoading, setIconosLoading] = useState(false);
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  // "todas" = listado paginado de rastreo_bo; "sin" = unidades sin usuario directo
  // ni grupo (las unicas que se pueden eliminar). Esa lista se trae una sola vez
  // completa (tarda) y se busca/pagina aca.
  const [vista, setVista] = useState("todas");
  const [sinUsuarios, setSinUsuarios] = useState(null);
  const [cargandoSin, setCargandoSin] = useState(false);

  const cargarSin = useCallback(async () => {
    setCargandoSin(true);
    setError(null);
    try {
      const res = await listarVehiculosSinUsuarios();
      const data = res?.data?.data ?? [];
      setSinUsuarios(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(getErrorMessage(e));
      setSinUsuarios([]);
    } finally {
      setCargandoSin(false);
    }
  }, []);

  useEffect(() => { if (vista === "sin" && sinUsuarios === null) cargarSin(); }, [vista, sinUsuarios, cargarSin]);

  const sinFiltrados = useMemo(() => {
    const q = buscarActivo.toLowerCase();
    return (sinUsuarios || []).filter((v) => !q
      || [v.id, v.veh_placa, v.veh_alias, v.veh_modid].some((x) => String(x ?? "").toLowerCase().includes(q)));
  }, [sinUsuarios, buscarActivo]);

  const esSin = vista === "sin";
  const filas = esSin ? sinFiltrados.slice(page * pageSize, (page + 1) * pageSize) : vehiculos;
  const totalVista = esSin ? sinFiltrados.length : total;
  const cargandoVista = esSin ? cargandoSin : loading;

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listarVehiculos({ page: page + 1, pageSize, buscar: buscarActivo });
      const body = res?.data;
      const data = body?.data ?? [];
      setVehiculos(Array.isArray(data) ? data : []);
      setTotal(Number(body?.total || 0));
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, buscarActivo]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleBuscar = () => {
    setPage(0);
    setBuscarActivo(buscar.trim());
  };

  const abrirCrear = () => {
    setEditId(null);
    setCreateForm(emptyCreateForm);
    setErrorCrear(null);
    setCreateOpen(true);
  };

  const abrirEditarVehiculo = (v) => {
    setEditId(v.id);
    setCreateForm({
      veh_alias: v.veh_alias || "",
      veh_placa: v.veh_placa || "",
      veh_modid: v.veh_modid || "",
      veh_observaciones: v.veh_observaciones || "",
      veh_icono: v.veh_icono || "",
      veh_activo: v.veh_activo !== false,
    });
    setErrorCrear(null);
    setCreateOpen(true);
  };

  const abrirSelectorIcono = async () => {
    setIconPickerOpen(true);
    if (iconosCargados) return;
    setIconosLoading(true);
    try {
      const res = await listarIconos();
      const data = res?.data?.data ?? res?.data;
      setIconos(Array.isArray(data) ? data : []);
      setIconosCargados(true);
    } catch (e) {
      setIconos([]);
    } finally {
      setIconosLoading(false);
    }
  };

  const seleccionarIcono = (url) => {
    setCreateForm((p) => ({ ...p, veh_icono: url }));
    setIconPickerOpen(false);
  };

  const guardarFormVehiculo = async () => {
    const placa = createForm.veh_placa.trim();
    const modid = createForm.veh_modid.trim();
    if (!placa || !modid) {
      setErrorCrear("Placa y Modem/IMEI son obligatorios.");
      return;
    }
    setCreando(true);
    setErrorCrear(null);
    try {
      const payload = {
        veh_alias: createForm.veh_alias.trim() || placa,
        veh_placa: placa,
        veh_modid: modid,
        veh_observaciones: createForm.veh_observaciones.trim(),
        veh_icono: createForm.veh_icono.trim(),
        veh_activo: createForm.veh_activo,
      };
      if (editId) await editarVehiculo(editId, payload);
      else await crearVehiculo(payload);
      setCreateOpen(false);
      await (esSin ? cargarSin() : cargar());
    } catch (e) {
      setErrorCrear(getErrorMessage(e));
    } finally {
      setCreando(false);
    }
  };

  const confirmarEliminar = async () => {
    setEliminando(true);
    setErrorEliminar("");
    try {
      await eliminarVehiculo(eliminar.id);
      // Se quita de la lista ya cargada, sin volver a recorrer todo rastreo_bo
      setSinUsuarios((prev) => (prev || []).filter((x) => x.id !== eliminar.id));
      setEliminar(null);
    } catch (e) {
      setErrorEliminar(getErrorMessage(e));
    } finally {
      setEliminando(false);
    }
  };

  return (
    <DashboardLayout>
      <PageHeader
        title="TRS Rastreo — Vehiculos"
        subtitle="Administracion y seguimiento de vehiculos"
        backTo="/trsrastreo"
        actions={(
          <Stack direction="row" spacing={1}>
            <Button size="small" startIcon={<RefreshIcon />} onClick={esSin ? cargarSin : cargar} disabled={cargandoVista}>
              Actualizar
            </Button>
            <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={abrirCrear} disabled={loading}>
              Registrar vehiculo
            </Button>
          </Stack>
        )}
      />

      <Box sx={{ p: { xs: 1, md: 1.5 } }}>
        <Tabs
          value={vista}
          onChange={(_, v) => { setVista(v); setPage(0); }}
          sx={{ minHeight: 36, mb: 1, borderBottom: 1, borderColor: "divider", "& .MuiTab-root": { minHeight: 36, textTransform: "none", fontWeight: 700, fontSize: "0.85rem" } }}
        >
          <Tab value="todas" label="Todas las unidades" />
          <Tab
            value="sin"
            label={(
              <Stack direction="row" spacing={0.75} alignItems="center">
                <span>Sin usuarios relacionados</span>
                {sinUsuarios && <Chip size="small" label={sinUsuarios.length} sx={{ height: 18, fontSize: "0.68rem", fontWeight: 800 }} />}
              </Stack>
            )}
          />
        </Tabs>

        {esSin && (
          <Alert severity="info" sx={{ mb: 1, py: 0 }}>
            Unidades que no están vinculadas a ningún usuario, ni directo ni a través de un grupo. Solo estas se pueden eliminar.
            {cargandoSin && " Revisando todas las unidades de TRS Rastreo, puede tardar unos segundos…"}
          </Alert>
        )}

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mb: 1 }}>
          <TextField
            size="small"
            label="Buscar"
            placeholder="Placa, alias o modem"
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleBuscar(); }}
            sx={{ ...compactFieldSx, width: 280 }}
          />
          <Button size="small" variant="contained" startIcon={<SearchIcon />} onClick={handleBuscar} sx={{ minHeight: 32 }}>
            Buscar
          </Button>
          <Typography variant="caption" color="text.secondary">
            {filas.length} en esta pagina — {totalVista} en total
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

        <Paper variant="outlined" sx={{ borderRadius: 1.25, overflow: "hidden" }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <StyledHeaderCell sx={{ width: 44 }} />
                  <StyledHeaderCell>ID</StyledHeaderCell>
                  <StyledHeaderCell>Placa</StyledHeaderCell>
                  <StyledHeaderCell>Alias</StyledHeaderCell>
                  <StyledHeaderCell>Modem</StyledHeaderCell>
                  <StyledHeaderCell align="center">Usuarios</StyledHeaderCell>
                  <StyledHeaderCell>Ultima posicion</StyledHeaderCell>
                  <StyledHeaderCell align="center">Estado</StyledHeaderCell>
                  <StyledHeaderCell align="center">Acciones</StyledHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cargandoVista ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={26} />
                    </TableCell>
                  </TableRow>
                ) : filas.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">No hay vehiculos para mostrar.</Typography>
                    </TableCell>
                  </TableRow>
                ) : filas.map((v) => {
                  const usuariosActivos = (v.usuarios_relacionados || []).filter((r) => r.estado_relacion).length;
                  return (
                    <TableRow key={v.id} hover sx={v.veh_activo ? {} : { opacity: 0.6 }}>
                      <TableCell sx={{ ...cellSx, width: 44 }}>
                        <Tooltip title="Ver en el mapa">
                          <Avatar
                            src={v.veh_icono || undefined}
                            alt={v.veh_placa}
                            variant="rounded"
                            onClick={() => setMapaVehiculo(v)}
                            sx={{ width: 28, height: 28, bgcolor: "grey.200", cursor: "pointer" }}
                          >
                            <DirectionsCarIcon sx={{ fontSize: 16, color: "grey.500" }} />
                          </Avatar>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={cellSx}>{v.id}</TableCell>
                      <TableCell sx={cellSx}>
                        <Typography variant="body2" fontWeight={700} sx={{ fontSize: "0.8rem" }}>{v.veh_placa}</Typography>
                      </TableCell>
                      <TableCell sx={cellSx}>{v.veh_alias || "-"}</TableCell>
                      <TableCell sx={cellSx}>
                        {v.veh_modid ? (
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => setEquipoDialog({ tipo: "modem", identificador: v.veh_modid })}
                            sx={{
                              p: 0, minWidth: 0, fontSize: "0.78rem", fontFamily: "monospace",
                              fontWeight: 700, textTransform: "none", justifyContent: "flex-start",
                            }}
                          >
                            {v.veh_modid}
                          </Button>
                        ) : (
                          <Typography variant="body2" sx={{ fontSize: "0.78rem" }}>-</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={cellSx} align="center">
                        {(v.usuarios_relacionados || []).length ? (
                          <Chip
                            icon={<PersonIcon />}
                            label={`${usuariosActivos}/${v.usuarios_relacionados.length}`}
                            size="small"
                            color={usuariosActivos ? "info" : "default"}
                            variant="outlined"
                            sx={{ height: 22, fontSize: "0.72rem", fontWeight: 700, "& .MuiChip-icon": { fontSize: 14, ml: "6px" } }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={cellSx}>
                        {v.device?.d_date_server ? (
                          <>
                            <Typography variant="body2" sx={{ fontSize: "0.78rem" }}>
                              {formatFecha(v.device.d_date_server)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {v.device.d_speed != null ? `${v.device.d_speed} km/h` : "-"} · {v.device.d_origin || "-"}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="caption" color="text.disabled">Sin datos</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={cellSx} align="center">
                        <Chip
                          label={v.veh_activo ? "Activo" : "Inactivo"}
                          size="small"
                          color={v.veh_activo ? "success" : "default"}
                          sx={{ height: 20, fontSize: "0.68rem" }}
                        />
                      </TableCell>
                      <TableCell sx={cellSx} align="center">
                        <Tooltip title="Ver detalle">
                          <IconButton size="small" onClick={() => setDetalle(v)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar vehiculo">
                          <IconButton size="small" onClick={() => abrirEditarVehiculo(v)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {esSin && (
                          <Tooltip title="Eliminar vehiculo">
                            <IconButton size="small" color="error" onClick={() => { setErrorEliminar(""); setEliminar(v); }}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalVista}
            page={page}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[15, 20, 30, 50, 100]}
            labelRowsPerPage="Limite"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
          />
        </Paper>
      </Box>

      <ConfirmDeleteDialog
        open={!!eliminar}
        loading={eliminando}
        titulo="Eliminar unidad"
        onClose={() => setEliminar(null)}
        description={
          errorEliminar
            || `¿Eliminar la unidad ${eliminar?.veh_placa || eliminar?.veh_alias || ""} de TRS Rastreo? Esta acción no se puede deshacer.`
        }
        onConfirm={confirmarEliminar}
      />

      <Dialog open={Boolean(detalle)} onClose={() => setDetalle(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "1rem", fontWeight: 800 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar
              src={detalle?.veh_icono || undefined}
              alt={detalle?.veh_placa}
              variant="rounded"
              sx={{ width: 40, height: 40, bgcolor: "grey.200" }}
            >
              <DirectionsCarIcon sx={{ color: "grey.500" }} />
            </Avatar>
            <span>{detalle?.veh_placa} {detalle?.veh_alias ? `· ${detalle.veh_alias}` : ""}</span>
          </Box>
          <IconButton size="small" onClick={() => setDetalle(null)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="caption" color="text.secondary">Modem / Observaciones</Typography>
              <Box>
                {detalle?.veh_modid ? (
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setEquipoDialog({ tipo: "modem", identificador: detalle.veh_modid })}
                    sx={{ p: 0, minWidth: 0, fontSize: "0.85rem", fontWeight: 700, textTransform: "none" }}
                  >
                    {detalle.veh_modid}
                  </Button>
                ) : (
                  <Typography variant="body2" component="span">-</Typography>
                )}
                {detalle?.veh_observaciones && (
                  <Typography variant="body2" component="span"> — {detalle.veh_observaciones}</Typography>
                )}
              </Box>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" fontWeight={800} sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                <SpeedIcon fontSize="small" /> Telemetria
              </Typography>
              {!detalle?.device?.d_date_server ? (
                <Typography variant="caption" color="text.secondary">Sin datos de telemetria</Typography>
              ) : (
                <Stack spacing={0.25}>
                  <Typography variant="body2">Fuente: {detalle.device.d_origin || "-"} ({detalle.device.d_name || "-"})</Typography>
                  <Typography variant="body2">Velocidad: {detalle.device.d_speed ?? "-"} km/h</Typography>
                  <Typography variant="body2">
                    Posicion: {detalle.device.d_latitude ?? "-"}, {detalle.device.d_longitude ?? "-"}
                  </Typography>
                  <Typography variant="body2">Satelites: {detalle.device.d_satellites ?? "-"}</Typography>
                  <Typography variant="body2">Ultimo reporte: {formatFecha(detalle.device.d_date_server)}</Typography>
                </Stack>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" fontWeight={800} sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                <PersonIcon fontSize="small" /> Usuarios relacionados ({(detalle?.usuarios_relacionados || []).length})
              </Typography>
              {(detalle?.usuarios_relacionados || []).length === 0 ? (
                <Typography variant="caption" color="text.secondary">Sin usuarios relacionados</Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={cellSx}>Usuario</TableCell>
                      <TableCell sx={cellSx}>Cliente</TableCell>
                      <TableCell sx={cellSx} align="center">Relacion</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detalle.usuarios_relacionados.map((r) => (
                      <TableRow key={r.usuariovehiculo_id}>
                        <TableCell sx={cellSx}>
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.78rem" }}>{r.usuario?.usu_nombre}</Typography>
                          <Typography variant="caption" color="text.secondary">{r.usuario?.usu_username}</Typography>
                        </TableCell>
                        <TableCell sx={cellSx}>{r.usuario?.usu_cliente ?? "-"}</TableCell>
                        <TableCell sx={cellSx} align="center">
                          <Chip
                            label={r.estado_relacion ? "Activa" : "Inactiva"}
                            size="small"
                            color={r.estado_relacion ? "success" : "default"}
                            sx={{ height: 18, fontSize: "0.62rem" }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(mapaVehiculo)} onClose={() => setMapaVehiculo(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar
              src={mapaVehiculo?.veh_icono || undefined}
              alt={mapaVehiculo?.veh_placa}
              variant="rounded"
              sx={{ width: 32, height: 32, bgcolor: "grey.200" }}
            >
              <DirectionsCarIcon sx={{ fontSize: 18, color: "grey.500" }} />
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              Ubicacion del vehiculo
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {mapaVehiculo?.veh_placa} {mapaVehiculo?.veh_alias ? `· ${mapaVehiculo.veh_alias}` : ""} — {formatFecha(mapaVehiculo?.device?.d_date_server)}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 1 }}>
          {mapaVehiculo?.device?.d_latitude != null && mapaVehiculo?.device?.d_longitude != null
            && !(mapaVehiculo.device.d_latitude === 0 && mapaVehiculo.device.d_longitude === 0) ? (
            <Box>
              <Box sx={{ mb: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Chip size="small" label={`Lat: ${mapaVehiculo.device.d_latitude}`} />
                <Chip size="small" label={`Lng: ${mapaVehiculo.device.d_longitude}`} />
                {mapaVehiculo.device.d_speed != null && (
                  <Chip size="small" label={`${mapaVehiculo.device.d_speed} km/h`} />
                )}
                <Button
                  size="small"
                  target="_blank"
                  rel="noreferrer"
                  href={`https://www.google.com/maps?q=${mapaVehiculo.device.d_latitude},${mapaVehiculo.device.d_longitude}`}
                >
                  Abrir en Google Maps
                </Button>
              </Box>
              <Box
                component="iframe"
                title="Mapa"
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${mapaVehiculo.device.d_longitude - 0.01}%2C${mapaVehiculo.device.d_latitude - 0.01}%2C${mapaVehiculo.device.d_longitude + 0.01}%2C${mapaVehiculo.device.d_latitude + 0.01}&layer=mapnik&marker=${mapaVehiculo.device.d_latitude}%2C${mapaVehiculo.device.d_longitude}`}
                sx={{ width: "100%", height: 420, border: "1px solid #d1d5db", borderRadius: 1 }}
              />
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Este vehiculo no tiene coordenadas disponibles.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 0.75 }}>
          <Button size="small" onClick={() => setMapaVehiculo(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <CentroEquipoDetalleDialog
        open={!!equipoDialog}
        tipo={equipoDialog?.tipo || "modem"}
        identificador={equipoDialog?.identificador}
        onClose={() => setEquipoDialog(null)}
      />

      <Dialog open={createOpen} onClose={() => !creando && setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: "1rem", fontWeight: 900 }}>
          {editId ? "Editar vehiculo" : "Registrar vehiculo"}
        </DialogTitle>
        <DialogContent dividers sx={{ display: "grid", gap: 1.25, pt: 1.5 }}>
          <TextField
            size="small"
            label="Placa"
            value={createForm.veh_placa}
            onChange={(e) => setCreateForm((p) => ({ ...p, veh_placa: e.target.value }))}
            disabled={creando}
            autoFocus
            required
          />
          <TextField
            size="small"
            label="Alias"
            value={createForm.veh_alias}
            onChange={(e) => setCreateForm((p) => ({ ...p, veh_alias: e.target.value }))}
            disabled={creando}
            helperText="Si se deja vacio, se usa la placa"
          />
          <TextField
            size="small"
            label="Modem / IMEI"
            value={createForm.veh_modid}
            onChange={(e) => setCreateForm((p) => ({ ...p, veh_modid: e.target.value }))}
            disabled={creando}
            required
          />
          <TextField
            size="small"
            label="Observaciones"
            value={createForm.veh_observaciones}
            onChange={(e) => setCreateForm((p) => ({ ...p, veh_observaciones: e.target.value }))}
            disabled={creando}
            multiline
            minRows={2}
          />
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>Icono</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Avatar
                src={createForm.veh_icono || undefined}
                variant="rounded"
                sx={{ width: 40, height: 40, bgcolor: "grey.200" }}
              >
                <DirectionsCarIcon sx={{ color: "grey.500" }} />
              </Avatar>
              <Button size="small" variant="outlined" onClick={abrirSelectorIcono} disabled={creando}>
                Elegir icono
              </Button>
              {createForm.veh_icono && (
                <Button size="small" color="inherit" onClick={() => setCreateForm((p) => ({ ...p, veh_icono: "" }))} disabled={creando}>
                  Quitar
                </Button>
              )}
            </Box>
          </Box>
          <FormControlLabel
            control={(
              <Checkbox
                checked={createForm.veh_activo}
                onChange={(e) => setCreateForm((p) => ({ ...p, veh_activo: e.target.checked }))}
                disabled={creando}
              />
            )}
            label="Activo"
          />

          {errorCrear && <Alert severity="error">{errorCrear}</Alert>}
        </DialogContent>
        <DialogActions sx={{ p: 1.5 }}>
          <Button size="small" onClick={() => setCreateOpen(false)} disabled={creando}>Cancelar</Button>
          <Button
            size="small"
            variant="contained"
            onClick={guardarFormVehiculo}
            disabled={creando}
            startIcon={creando ? <CircularProgress size={14} color="inherit" /> : null}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={iconPickerOpen} onClose={() => setIconPickerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "1rem", fontWeight: 800 }}>
          Elegir icono
          <IconButton size="small" onClick={() => setIconPickerOpen(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {iconosLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={26} />
            </Box>
          ) : iconos.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No hay iconos disponibles.</Typography>
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {iconos.map((url) => (
                <Tooltip key={url} title={url.split("/").pop()}>
                  <Avatar
                    src={url}
                    variant="rounded"
                    onClick={() => seleccionarIcono(url)}
                    sx={{
                      width: 44,
                      height: 44,
                      cursor: "pointer",
                      bgcolor: "grey.100",
                      border: "2px solid",
                      borderColor: createForm.veh_icono === url ? "primary.main" : "transparent",
                    }}
                  />
                </Tooltip>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
